import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../context/I18nContext';
import LanguageSwitcher from '../components/LanguageSwitcher';

const Login = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { authenticated } = useAuth();
    const { t } = useI18n(['common', 'login']);
    
    const [message, setMessage] = useState('');

    // from location state or query get from
    const from = location.state?.from?.pathname || '/home';

    useEffect(() => {
        // if already authenticated, redirect to 'from' location
        if (authenticated) {
            navigate(from, { replace: true });
        }

        // 从 URL query 获取讯息
        const params = new URLSearchParams(location.search);
        const error = params.get('error');
        if (error) {
            const errorMessages = {
                'invalid_callback': t('invalidCallback', { ns: 'login' }),
                'invalid_state': t('invalidState', { ns: 'login' }),
                'auth_failed': t('authFailed', { ns: 'login' }),
                'session_failed': t('sessionFailed', { ns: 'login' })
            };
            setMessage(errorMessages[error] || t('loginFailed', { ns: 'login' }));
        }
        
        const urlMessage = params.get('message');
        if (urlMessage) {
            setMessage(decodeURIComponent(urlMessage));
        }
    }, [authenticated, navigate, from, location.search, t]);

    const handleMicrosoftLogin = () => {
        // 重定向到 Entra ID 登入
        window.location.href = `/auth/entra?from=${encodeURIComponent(from)}`;
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-linear-to-br from-blue-50 to-gray-100 px-4">
            {/* Language Switcher in top right corner */}
            <div className="absolute top-4 right-4">
                <LanguageSwitcher />
            </div>
            
            <div className="max-w-md w-full bg-white rounded-lg shadow-xl p-8">
                <div className="mb-4">
                    <Link 
                        to="/" 
                        className="inline-flex items-center text-sm text-gray-600 hover:text-blue-600 transition-colors"
                    >
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                        </svg>
                        {t('back', { ns: 'login' })}
                    </Link>
                </div>

                <div className="text-center mb-8">
                    <h1 className="text-3xl font-bold text-gray-900 mb-2">{t('welcomeBack', { ns: 'login' })}</h1>
                    <p className="text-gray-600">{t('signInWith', { ns: 'login' })}</p>
                </div>

                {message && (
                    <div className="mb-6 p-3 bg-red-100 border border-red-400 text-red-700 rounded-md">
                        {message}
                    </div>
                )}

                <button
                    onClick={handleMicrosoftLogin}
                    className="w-full flex items-center justify-center gap-3 bg-white hover:bg-gray-50 text-gray-700 font-semibold py-3 px-4 rounded-md border-2 border-gray-300 hover:border-blue-500 transition-all shadow-sm cursor-pointer"
                >
                    <svg className="w-6 h-6" viewBox="0 0 23 23" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M0 0H10.8696V10.8696H0V0Z" fill="#F25022"/>
                        <path d="M12.1304 0H23V10.8696H12.1304V0Z" fill="#7FBA00"/>
                        <path d="M0 12.1304H10.8696V23H0V12.1304Z" fill="#00A4EF"/>
                        <path d="M12.1304 12.1304H23V23H12.1304V12.1304Z" fill="#FFB900"/>
                    </svg>
                    {t('signInButton', { ns: 'login' })}
                </button>

                <p className="mt-6 text-center text-sm text-gray-600">
                    {t('useOrgAccount', { ns: 'login' })}
                </p>
            </div>
        </div>
    );
};

export default Login;
